import React, { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import styled, { withTheme } from 'styled-components';

import {
  Expando,
  Text,
  useMedia,
  VerticalSpacing,
  Box,
} from '../../../Boneyard';

import NavItem from './NavItem';
import { TrackClick } from '@g4/trackster';

const propTypes = {
  forceExpanded: PropTypes.bool,
  item: PropTypes.object.isRequired,
  defaultBackgroundType: PropTypes.oneOf(['dark', 'light']),
  theme: PropTypes.object.isRequired,
  automationHook: PropTypes.string,
  isAllegiantStadiumNavItems: PropTypes.bool,
  children: PropTypes.oneOfType([PropTypes.func, PropTypes.object]),
  isCustomMenu: PropTypes.bool,
  alignRight: PropTypes.bool,
  isMultiLineItem: PropTypes.bool,
};

const defaultProps = {
  defaultBackgroundType: 'light',
  isAllegiantStadiumNavItems: false,
  isMultiLineItem: false,
};

const StyledExpando = styled(Expando)`
  width: 100%;
  position: ${props => (props.isMegaMenu ? 'static' : 'relative')};

  .ReactCollapse--content {
    font-size: ${props => props.theme.toRem(14)};
  }

  ${props =>
    props.displayAsDropdown &&
    `> div > .ReactCollapse--collapse {
      position: absolute;
      right: ${props.alignRight ? '0' : ''};
      z-index: -1;
      width: ${props.alignRight ? 'auto' : '100%'};
      position: ${props.isMobile ? 'relative' : 'absolute'};
      left: ${props.isMobile ? '20px' : props.alignRight ? 'auto' : '0'};
      ${props => props.theme.query.xs} {
        z-index: 1;
      }
      ${props.open &&
        !props.theme.query.xs &&
        `box-shadow: 0 1px 5px 0 props.theme.colors.siteHeader.light.linkBackground;`}
  }`}

  ${props =>
    props.displayAsDropdown &&
    props.theme.query.xs &&
    `> div > .ReactCollapse--collapse > div > ul > li > a {
      font-size: 0.725rem;
  }`}
`;

const ExpandableMenuItem = ({
  forceExpanded,
  item,
  defaultBackgroundType,
  isAllegiantStadiumNavItems,
  theme,
  automationHook,
  children,
  isCustomMenu,
  alignRight,
  isMultiLineItem,
}) => {
  const mq = useMedia();
  const isMobile = mq.lte('sm');
  const [isOpen, setIsOpen] = React.useState(false);

  const toggleIsOpen = () => setIsOpen(prevState => !prevState);
  const closeMenu = () => setIsOpen(false);

  const { items, expanded, title, trackLabel } = item;
  const ref = useRef(null);

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, false);
    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  });

  const handleClickOutside = event => {
    if (ref.current && !ref.current.contains(event.target)) {
      closeMenu();
    }
  };

  const isMegaMenu =
    items &&
    items.length &&
    items.every(item => item.__typename === 'GroupMenuItem');

  if (!expanded && !forceExpanded) {
    return (
      <div ref={ref} onChange={closeMenu}>
        <StyledExpando
          label={
            <TrackClick category="site_click_tracking" action="Top Nav" label={trackLabel || title}>
              <NavItem
                item={item}
                isCustomMenu={isCustomMenu}
                isOpen={isOpen}
                onClick={toggleIsOpen}
                defaultBackgroundType={defaultBackgroundType}
                isAllegiantStadiumNavItems={isAllegiantStadiumNavItems}
                automationHook={automationHook}
                isMultiLineItem={isMultiLineItem}
                showArrow
              />
            </TrackClick>
          }
          open={isOpen}
          a11yLabel={`Toggle ${title}`}
          icon="NONE"
          displayAsDropdown={true}
          isMegaMenu={isMegaMenu}
          isMobile={isMobile}
          alignRight={alignRight}
        >
          {isOpen
            ? isCustomMenu
              ? children
              : children({
                  displayAsColumn: !isMegaMenu,
                  displayAsDropdown: true,
                  isMegaMenu,
                })
            : null}
        </StyledExpando>
      </div>
    );
  }

  return (
    <div>
      <VerticalSpacing size={2} />
      <Text
        size={isMobile ? 16 : 20}
        color={theme.colors.siteHeader.light.linkColor}
        automationHook={automationHook}
      >
        {title}
      </Text>
      <VerticalSpacing size={2} />
      <Box ml={4}>
        <Text size={isMobile ? 12 : 14}>
          {isCustomMenu
            ? children
            : children({ displayAsColumn: true, isMegaMenuLink: true })}
        </Text>
      </Box>
      <VerticalSpacing size={2} />
    </div>
  );
};

ExpandableMenuItem.propTypes = propTypes;
ExpandableMenuItem.defaultProps = defaultProps;

export default withTheme(ExpandableMenuItem);
